import { Box, Link as MuiLink, Typography } from '@mui/material';
import React from 'react';

import {
  SUBSCRIPTION_TERMS_OF_USE_LABEL,
  SUBSCRIPTION_TERMS_OF_USE_PDF_PATH,
} from '../config/legalDocuments';
import { isIosNativeApp } from '../utils/nativeCheckoutBridge';

export interface SubscriptionTermsDisclosureProps {
  /** Trial wording, ideally StoreKit's on iOS. Omit when no trial is offered. */
  trialLabel?: string | null;
  /** Billing frequency wording, e.g. "$29.99/month". Omit when price is shown elsewhere on screen. */
  priceLabel?: string;
  compact?: boolean;
}

/**
 * The pre-purchase Subscription Summary required by the SMSP Subscription
 * Terms and by App Store review: duration, price, trial terms, auto-renewal,
 * how to cancel, and a link to the Terms of Use (EULA).
 *
 * This must render on the same screen as the button that starts a purchase.
 */
const SubscriptionTermsDisclosure: React.FC<SubscriptionTermsDisclosureProps> = ({
  trialLabel,
  priceLabel,
  compact = false,
}) => {
  const native = isIosNativeApp();
  const cancelCopy = native
    ? 'Manage or cancel any time in your Apple Account settings, at least 24 hours before the period ends.'
    : 'Cancel any time from Account → Subscriptions. Access continues through the end of the current paid period.';

  return (
    <Box
      sx={{ mt: compact ? 1 : 1.5 }}
      data-testid="subscription-terms-disclosure"
    >
      <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
        {trialLabel ? `${trialLabel}, then ` : ''}
        {priceLabel ? <strong>{priceLabel}</strong> : 'the price shown for your selected plan'}. Payment is charged
        {native ? ' to your Apple Account' : ' to your payment method'} at confirmation
        {trialLabel ? ' when the free trial ends' : ''}. The subscription renews automatically
        until canceled. {cancelCopy}
      </Typography>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
        <MuiLink
          href={SUBSCRIPTION_TERMS_OF_USE_PDF_PATH}
          target="_blank"
          rel="noopener noreferrer"
          data-attr="subscription-terms_eula-link"
        >
          {SUBSCRIPTION_TERMS_OF_USE_LABEL}
        </MuiLink>
      </Typography>
    </Box>
  );
};

export default SubscriptionTermsDisclosure;
